/**
 * Os dez estados obrigatórios do §8 da spec 02, como componentes.
 *
 * Eles moram aqui, e não em cada tela, por um motivo escrito na própria spec: *"nenhum é opcional, e
 * 'não pensei nesse' é o modo como cada um deles vira suporte"*. Componente compartilhado transforma
 * "não pensei" em "não importei" — que aparece na revisão.
 *
 * Duas regras atravessam todos:
 *
 * - **Estado nunca é comunicado só por cor** (§10). Verde/amarelo/vermelho sempre com rótulo, porque
 *   metade dos alertas desta operação é sobre qualidade de número e cor sozinha exclui daltônico.
 * - **Toda mensagem diz o que FAZER**, não o que aconteceu. É a mesma regra do `Veredito.explicacao`
 *   do domínio, aplicada à tela.
 */

import { useState, type ReactNode } from "react";
import { AlertTriangle, Ban, Inbox, Lock, RefreshCw, ShieldOff, Sparkles, WifiOff } from "lucide-react";
import { textos } from "./textos.js";
import type { FalhaDaApi } from "./api.js";
import { sairDaSessao } from "./sessao.js";

// --- Moldura ----------------------------------------------------------------------------------

interface MolduraProps {
  readonly icone: ReactNode;
  readonly titulo: string;
  readonly children?: ReactNode;
  readonly acao?: ReactNode;
  readonly tom?: "neutro" | "atencao" | "erro";
  readonly papel?: "status" | "alert";
}

function Moldura(props: MolduraProps) {
  const tom = props.tom ?? "neutro";
  const cor =
    tom === "erro"
      ? "text-[color:var(--erro-texto)]"
      : tom === "atencao"
        ? "text-[color:var(--atencao-texto)]"
        : "text-[color:var(--tinta-fraca)]";

  return (
    <div
      // `alert` interrompe o leitor de tela; `status` é anunciado gentilmente. Erro que exige ação
      // usa `alert`; vazio e carregando, `status`.
      role={props.papel ?? "status"}
      // O tom marcado: a trava "a tela não rola" reprova a tela que abre no estado de erro (spec 22).
      data-tom={props.tom}
      className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center"
    >
      <div className={cor} aria-hidden="true">
        {props.icone}
      </div>
      {/* `balance` SÓ no estreito, onde a quebra é inevitável: no largo ele partiria em duas uma frase que
          cabe em uma. No celular ele evita a palavra pendurada ("…não tem o Hugi / ativo"). */}
      <p className="text-base font-medium text-[color:var(--tinta-forte)] max-sm:text-balance">{props.titulo}</p>
      {props.children ? (
        <div className="max-w-prose text-sm text-[color:var(--tinta-fraca)] max-sm:text-balance">{props.children}</div>
      ) : null}
      {props.acao}
    </div>
  );
}

export function Botao(props: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  /** `perigo` é para o que não volta sozinho — revogar, desativar, remover. Vermelho claro, tonal. */
  readonly tipo?: "primario" | "secundario" | "perigo";
  readonly desabilitado?: boolean;
  /** **Obrigatório quando desabilitado** — ver `EstadoSomenteLeitura`. */
  readonly motivoDeDesabilitar?: string;
}) {
  const tipo = props.tipo ?? "primario";
  const primario = tipo === "primario";
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.desabilitado}
      // Um controle desabilitado sem motivo é um beco sem saída: a pessoa vê que não pode e não sabe
      // o que fazer. O §8 exige o motivo no `title`.
      title={props.desabilitado ? props.motivoDeDesabilitar : undefined}
      aria-disabled={props.desabilitado}
      /*
        O BOTÃO DO KIT (spec 23, segunda rodada): até 01/11 este botão tinha desenho próprio (tonal, raio 9) e convivia
        na mesma tela com o `v2-botao` do kit — dois "Salvar" diferentes lado a lado. Agora é um só desenho.

        ⚠️ O que o owner pediu em 03/09 continua valendo: *"botão branco na tela branca"* não pode. O secundário do
        kit tem borda visível e, no miolo, fundo próprio (`v2-visual.css`); o de perigo é contorno vermelho.
      */
      className={["v2-botao", primario ? "is-primario" : tipo === "perigo" ? "is-perigo" : ""].filter(Boolean).join(" ")}
    >
      {props.children}
    </button>
  );
}

// --- Os dez -----------------------------------------------------------------------------------

/**
 * 1. Carregando — **esqueleto com a forma do conteúdo, não spinner central** (§8).
 *
 * Um spinner não diz nada sobre o que vem; um esqueleto na forma da lista diz "vem uma lista" e
 * evita o salto de layout que faz a pessoa clicar no item errado.
 */
export function EstadoCarregando(props: { readonly linhas?: number; readonly rotulo?: string }) {
  const linhas = props.linhas ?? 3;
  return (
    <div role="status" aria-busy="true" aria-live="polite" className="flex flex-col gap-3 p-4">
      <span className="sr-only">{props.rotulo ?? textos.estados.carregando}</span>
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-[color:var(--vidro-controle)]" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 animate-pulse rounded bg-[color:var(--vidro-controle)]" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-[color:var(--vidro-controle)]" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** 2. Vazio de verdade — **explica o que faria aparecer dado ali** (§8). */
export function EstadoVazio(props: { readonly titulo: string; readonly comoEncher: string; readonly acao?: ReactNode }) {
  return (
    <Moldura icone={<Inbox size={32} />} titulo={props.titulo} acao={props.acao}>
      {props.comoEncher}
    </Moldura>
  );
}

/**
 * 3. Vazio por escolha nossa — o inbox recém-conectado.
 *
 * O histórico do CoEx é **descartado por escolha** nossa, e a invariante 6 do §7 é explícita:
 * *"nunca renderizar histórico que não temos"*, e a tela **diz por quê**. Silêncio aqui vira chamado
 * de suporte no dia um — o cliente conecta, vê inbox vazio e conclui que não funcionou.
 */
export function EstadoVazioPorEscolha(props: { readonly children?: ReactNode }) {
  return (
    <Moldura icone={<Sparkles size={32} />} titulo={textos.estados.vazioPorEscolha.titulo}>
      {props.children ?? textos.estados.vazioPorEscolha.corpo}
    </Moldura>
  );
}

/**
 * 4. Erro recuperável — **sem perder o que foi digitado** (§8).
 *
 * A parte de não perder o texto não é deste componente: é de quem o renderiza, e por isso `aoRepetir`
 * existe em vez de um `window.location.reload()` conveniente. Recarregar a página é a forma mais
 * rápida de apagar o rascunho de um atendente.
 */
export function EstadoErroRecuperavel(props: {
  readonly falha: FalhaDaApi;
  readonly aoRepetir?: () => void;
}) {
  const indisponivel = props.falha.tipo === "indisponivel";
  const espera = indisponivel ? props.falha.tentarEmSegundos : null;

  return (
    <Moldura
      papel="alert"
      tom="erro"
      icone={indisponivel ? <WifiOff size={32} /> : <AlertTriangle size={32} />}
      titulo={indisponivel ? textos.estados.indisponivel.titulo : textos.estados.erro.titulo}
      acao={
        props.aoRepetir ? (
          <Botao tipo="secundario" onClick={props.aoRepetir}>
            <RefreshCw size={16} aria-hidden="true" />
            {textos.acoes.tentarDeNovo}
          </Botao>
        ) : null
      }
    >
      <p>{props.falha.erro.mensagem ?? textos.estados.erro.corpo}</p>
      {espera !== null ? <p className="mt-1">{textos.estados.indisponivel.espere(espera)}</p> : null}
      {/* O código nunca aparece sozinho (§9), e o traceId é copiável — é o que o suporte pede. */}
      <DetalheTecnico erro={props.falha.erro.erro} traceId={props.falha.erro.traceId} />
    </Moldura>
  );
}

/**
 * 5. Sem permissão — **diz qual permissão falta e quem no tenant pode conceder** (§8).
 *
 * "Você não tem acesso" é a mensagem que gera chamado. Nomear a permissão transforma o chamado numa
 * conversa de trinta segundos com o administrador da própria empresa.
 */
export function EstadoSemPermissao(props: { readonly permissao: string | null }) {
  return (
    <Moldura papel="alert" tom="atencao" icone={<Lock size={32} />} titulo={textos.estados.semPermissao.titulo}>
      {props.permissao ? textos.estados.semPermissao.qual(props.permissao) : textos.estados.semPermissao.semNome}
      <p className="mt-2">{textos.estados.semPermissao.quemConcede}</p>
    </Moldura>
  );
}

/**
 * 6. Sem entitlement — **caminho comercial, não 403 seco** (§5 regra 2), e **com saída**.
 *
 * ⚠️ O botão de trocar de conta mora AQUI, e não na casca: o painel e o inbox desenham esta tela no
 * lugar da casca inteira, então sem ele não havia Sair — quem entrasse pela empresa errada ficava preso
 * até apagar o cookie na mão (30/09).
 */
export function EstadoSemEntitlement(props: { readonly nomeDoTenant?: string | null; readonly acao?: ReactNode }) {
  const [saindo, setSaindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function trocarDeConta() {
    setErro(null);
    setSaindo(true);
    const falha = await sairDaSessao();
    if (falha) setErro(falha);
    setSaindo(false);
  }

  return (
    <Moldura
      icone={<ShieldOff size={32} />}
      titulo={textos.estados.semEntitlement.titulo(props.nomeDoTenant ?? null)}
      acao={
        <div className="flex flex-col items-center gap-2">
          <Botao onClick={trocarDeConta} desabilitado={saindo} motivoDeDesabilitar={textos.estados.semEntitlement.saindo}>
            {saindo ? textos.estados.semEntitlement.saindo : textos.estados.semEntitlement.trocarDeConta}
          </Botao>
          {erro ? (
            <p role="alert" className="text-xs text-[color:var(--erro-texto)]">
              {erro}
            </p>
          ) : null}
          {props.acao}
        </div>
      }
    >
      {textos.estados.semEntitlement.corpo}
    </Moldura>
  );
}

/**
 * 7. Canal revogado — **banner persistente**, não toast (§8 e §7.5).
 *
 * Toast desaparece; o canal continua revogado. E enquanto ele estiver revogado, o compositor tem de
 * estar desligado — canal revogado que parece ativo faz o operador digitar mensagem que nunca sai.
 */
export function BannerCanalRevogado(props: { readonly numero?: string | null; readonly aoReconectar?: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-3 border-b border-[color:var(--erro)] bg-[color:var(--erro-clara)] px-4 py-3 text-sm text-[color:var(--erro-texto)]"
    >
      <Ban size={18} aria-hidden="true" className="shrink-0" />
      <span className="flex-1">{textos.estados.canalRevogado(props.numero ?? null)}</span>
      {props.aoReconectar ? (
        <Botao tipo="secundario" onClick={props.aoReconectar}>
          {textos.acoes.reconectar}
        </Botao>
      ) : null}
    </div>
  );
}

/**
 * 8. Conteúdo expirado — a invariante 3 do §7, e a que mais parece bug quando ignorada.
 *
 * A retenção apaga o `texto` e **preserva a linha** (ADR-0003). Uma bolha vazia parece defeito ou
 * censura; a frase com data explica que foi política, e o carimbo de hora e o remetente continuam
 * ali — que é justamente o que o expurgo preserva de propósito.
 *
 * **Não usa `dangerouslySetInnerHTML`** — nem poderia: o texto vem de terceiro por definição (§11).
 */
export function ConteudoRemovido(props: { readonly removidoEm: Date | string; readonly diasDeRetencao?: number | null }) {
  const data = typeof props.removidoEm === "string" ? new Date(props.removidoEm) : props.removidoEm;
  return (
    <p className="text-sm italic text-[color:var(--tinta-fraca)]">
      {textos.estados.conteudoRemovido(data, props.diasDeRetencao ?? null)}
    </p>
  );
}

/**
 * O texto de uma mensagem — e o único caminho por onde ele deve chegar à tela.
 *
 * Existe para que a invariante 3 seja **impossível de esquecer**: quem renderiza `{m.texto}` direto
 * produz a bolha vazia. Aqui, `texto: null` com `expiraEm` no passado vira a frase de remoção; e
 * `texto: null` sem vencimento é mídia ou tipo sem texto, que é outra coisa e diz outra coisa.
 */
export function TextoDaMensagem(props: {
  readonly texto: string | null;
  readonly expiraEm?: string | Date | null;
  readonly agora?: Date;
  readonly diasDeRetencao?: number | null;
}) {
  if (props.texto !== null) {
    // `whitespace-pre-wrap`: quebra de linha que o consumidor digitou é conteúdo. `break-words`
    // porque URL longa de terceiro estoura o layout do fio.
    return <p className="whitespace-pre-wrap break-words text-sm text-[color:var(--tinta-forte)]">{props.texto}</p>;
  }

  const expira = props.expiraEm ? (typeof props.expiraEm === "string" ? new Date(props.expiraEm) : props.expiraEm) : null;
  const agora = props.agora ?? new Date();

  if (expira && expira.getTime() <= agora.getTime()) {
    return <ConteudoRemovido removidoEm={expira} diasDeRetencao={props.diasDeRetencao ?? null} />;
  }

  // Sem texto e sem vencimento passado: é mensagem que nunca teve texto — mídia, áudio, localização.
  // Dizer "sem texto" é honesto; reaproveitar a frase de retenção seria mentira sobre privacidade.
  return <p className="text-sm italic text-[color:var(--tinta-fraca)]">{textos.estados.semTexto}</p>;
}

/**
 * 9. Degradado — **nunca verde** (§7.10).
 *
 * O `/health` do servidor é deliberadamente honesto, e a tela herda: canal conectado com qualidade
 * caída é **amarelo com o número**, não "✅ Conectado". `rotulo` é obrigatório porque cor sozinha
 * não comunica (§10).
 */
export function Selo(props: { readonly estado: "ok" | "degradado" | "ruim" | "neutro"; readonly rotulo: string; readonly detalhe?: string }) {
  /*
    ---- A COR SEMÂNTICA VAI NO FUNDO E NUM PONTO; O TEXTO FICA LEGÍVEL ----

    Os três estados usavam a própria cor como TEXTO sobre a tinta clara dela, e os três reprovavam
    AA, medido na tela: "ruim" dava **2,61:1**, "degradado" **2,96:1**. Num selo que diz
    "Não enviado" isso é o pior lugar possível para economizar contraste.

    Escurecer o vermelho e o âmbar exigiria cores fora da palheta — e a catraca recusaria, com
    razão. A saída não custa nada: **cor como FORMA** (fundo + ponto), legibilidade no texto. O
    estado continua sendo reconhecido de relance pela cor, e a palavra continua legível de perto,
    que é justamente o que o §10 pede ao exigir `rotulo`.
  */
  const estilo = {
    ok: "bg-[color:var(--ok-clara)] text-[color:var(--tinta-forte)]",
    degradado: "bg-[color:var(--atencao-clara)] text-[color:var(--tinta-forte)]",
    ruim: "bg-[color:var(--erro-clara)] text-[color:var(--tinta-forte)]",
    neutro: "bg-[color:var(--vidro-controle)] text-[color:var(--tinta)]",
  }[props.estado];

  const ponto = {
    ok: "var(--ok-texto)",
    degradado: "var(--atencao-texto)",
    ruim: "var(--erro-texto)",
    neutro: "var(--tinta-fraca)",
  }[props.estado];

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${estilo}`}>
      {/* O ponto é decorativo — o rótulo ao lado é que informa —, e é ele que CARREGA a cor do
          estado agora que o texto virou tinta legível. Sem o `style`, ele herdaria a tinta e o selo
          perderia o sinal de relance. Decorativo não precisa de razão de contraste. */}
      <span aria-hidden="true" className="text-[8px] leading-none" style={{ color: ponto }}>
        ●
      </span>
      {props.rotulo}
      {/*
        ⚠️ SEM `opacity`, e o peso é que distingue.

        Era `font-normal opacity-80`, e a opacidade multiplicava uma tinta que já foi calculada para
        passar AA — derrubando o detalhe para **3,62:1** no tema claro. O `font-normal` ao lado do
        rótulo em `font-medium` já faz a hierarquia; a opacidade só tirava o contraste que o cálculo
        da cor do selo existe para garantir.
      */}
      {props.detalhe ? <span className="font-normal">· {props.detalhe}</span> : null}
    </span>
  );
}

/**
 * 10. Somente leitura — **controles visíveis e desabilitados, com o motivo no `title`** (§8).
 *
 * Atenção ao que a spec 05 §12 pede num caso específico: no fio de conversa, quem tem `read_conversation`
 * e não tem `:responder` vê o fio **sem compositor no DOM** — não um compositor desabilitado. São
 * regras diferentes de propósito: um botão desabilitado ensina que a ação existe; um compositor
 * desabilitado convida a digitar e depois recusa.
 */
export function EstadoSomenteLeitura(props: { readonly children: ReactNode; readonly motivo: string }) {
  return (
    <div className="flex flex-col gap-2">
      <p role="status" className="text-xs text-[color:var(--tinta-fraca)]">
        {props.motivo}
      </p>
      <div aria-disabled="true" className="pointer-events-none opacity-60">
        {props.children}
      </div>
    </div>
  );
}

// --- Auxiliares -------------------------------------------------------------------------------

/**
 * O detalhe técnico: **copiável, e nunca a mensagem principal** (§9).
 *
 * O cliente não sabe o que é `131047`, mas o suporte pede exatamente isso. `<details>` nativo em vez
 * de acordeão próprio: funciona com teclado e leitor de tela sem código nosso.
 */
export function DetalheTecnico(props: { readonly erro: string; readonly traceId?: string | undefined }) {
  const texto = props.traceId ? `${props.erro} · trace ${props.traceId}` : props.erro;
  return (
    <details className="mt-3 text-xs text-[color:var(--tinta-fraca)]">
      <summary className="cursor-pointer">{textos.acoes.detalheTecnico}</summary>
      <code className="mt-1 block select-all break-all rounded bg-[color:var(--vidro-controle)] px-2 py-1">
        {texto}
      </code>
    </details>
  );
}

/**
 * Traduz uma falha da API no estado de tela certo.
 *
 * Existe para que a decisão fique num lugar só. Cada tela escolhendo o seu estado a partir do status
 * HTTP é como uma delas manda a pessoa para o login por causa de um 503 — o erro exato que o H9
 * tomou trabalho para evitar no servidor.
 */
export function EstadoDaFalha(props: {
  readonly falha: FalhaDaApi;
  readonly aoRepetir?: () => void;
  readonly nomeDoTenant?: string | null;
}) {
  switch (props.falha.tipo) {
    case "sem_permissao":
      return <EstadoSemPermissao permissao={props.falha.permissao} />;
    case "sem_entitlement":
      return <EstadoSemEntitlement nomeDoTenant={props.nomeDoTenant ?? null} />;
    case "versao_incompativel":
      return (
        <Moldura
          papel="alert"
          tom="atencao"
          icone={<RefreshCw size={32} />}
          titulo={textos.estados.versaoIncompativel.titulo}
          acao={
            <Botao onClick={() => window.location.reload()}>{textos.acoes.recarregar}</Botao>
          }
        >
          {textos.estados.versaoIncompativel.corpo}
        </Moldura>
      );
    default:
      return <EstadoErroRecuperavel falha={props.falha} {...(props.aoRepetir ? { aoRepetir: props.aoRepetir } : {})} />;
  }
}
